import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type WikiSpaceSchema, wikiSpaceSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { WikiSpaceForm } from "@/forms/wiki-space-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditWikiSpaceModalProps extends ModalProps {
	spaceId: string;
}

export function EditWikiSpaceModal({ isOpen, onClose, spaceId }: EditWikiSpaceModalProps) {
	/** Hooks */
	const qc = useQueryClient();
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.wiki.spaces[":spaceId"].$get({
			param: { spaceId },
		});
		const data = await responseHandler(response);
		if (!data.wikiSpace) throw new Error("Wiki space not found");
		return data.wikiSpace;
	});
	const methods = useForm({
		defaultValues,
		resolver: resolver(wikiSpaceSchema),
	});

	/** Functions */
	async function handleOnSubmit(json: WikiSpaceSchema) {
		await client.wiki.spaces[":spaceId"].$patch({
			param: { spaceId },
			json,
		});
		await qc.invalidateQueries({
			predicate: invalidate(["wiki-spaces"]),
		});
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("wiki.edit_space")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<WikiSpaceForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
